import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useNotification } from "../contexts/NotificationContext";
import { researchApi, ActiveWork } from "../services/researchApi";
import {
  AgentActivity,
  AgentState,
  Criterion,
  FuzzyRubric,
  MonitoringConfig,
  Requirements,
  ResearchEntity,
  WorkflowStage,
  WorkflowState,
  WorkflowStatus,
} from "../types/researchWorkflow";
import { useTrackEntities } from "./queries/useTrackEntities";
import { useTrackActivities } from "./queries/useTrackActivities";
import { queryKeys } from "./queries/keys";
import { deriveStages } from "../utils/workflowStages";

export const useResearchWorkflowState = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  const [requirementsEditMode, setRequirementsEditMode] = useState(false);
  const [requirementsSaving, setRequirementsSaving] = useState(false);
  const prevLastModifiedRef = useRef<string | null>(null);
  const [state, setState] = useState<WorkflowState>({
    projectTitle: "Research Project",
    requirements: {
      objective: "",
      criteria: [],
    },
    entities: [],
    agentState: "idle",
    agentActivities: [],
    activeWork: { evaluation: { queued: 0, running: 0 }, discovery: [] },
    stages: {
      requirements: { stage: "requirements", status: "complete", counts: {} },
      longList: {
        stage: "longList",
        status: "not_started",
        counts: { total: 0, approved: 0, rejected: 0 },
      },
      shortList: {
        stage: "shortList",
        status: "not_started",
        counts: { shortlisted: 0 },
      },
      finalSelection: {
        stage: "finalSelection",
        status: "not_started",
        counts: { final: 0 },
      },
    },
    currentStage: "longList",
    selectedEntityId: undefined,
    overlayMode: undefined,
  });

  const entitiesQuery = useTrackEntities(
    currentTrackId,
    state.agentState === "running",
  );
  const activitiesQuery = useTrackActivities(
    currentTrackId,
    state.agentState === "running",
  );

  // Update requirements
  const updateRequirements = useCallback((requirements: Requirements) => {
    setState((prev) => ({
      ...prev,
      requirements,
    }));
  }, []);

  // Update objective
  const updateObjective = useCallback((objective: string) => {
    setState((prev) => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        objective,
      },
    }));
  }, []);

  // Add criterion
  const addCriterion = useCallback((criterion: Criterion) => {
    setState((prev) => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        criteria: [...prev.requirements.criteria, criterion],
      },
    }));
  }, []);

  // Update criterion
  const updateCriterion = useCallback(
    (criterionId: string, updates: Partial<Criterion>) => {
      setState((prev) => ({
        ...prev,
        requirements: {
          ...prev.requirements,
          criteria: prev.requirements.criteria.map((c) =>
            c.id === criterionId ? { ...c, ...updates } : c,
          ),
        },
      }));
    },
    [],
  );

  // Remove criterion
  const removeCriterion = useCallback((criterionId: string) => {
    setState((prev) => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        criteria: prev.requirements.criteria.filter(
          (c) => c.id !== criterionId,
        ),
      },
    }));
  }, []);

  // Change workflow stage
  const setCurrentStage = useCallback((stage: WorkflowStage) => {
    setState((prev) => ({
      ...prev,
      currentStage: stage,
    }));
  }, []);

  // Load requirements from backend
  const loadRequirements = useCallback(async (trackId: string) => {
    // Reset requirements immediately to prevent stale data
    setState((prev) => ({
      ...prev,
      requirements: {
        objective: "",
        criteria: [],
      },
      stages: {
        ...prev.stages,
        requirements: {
          ...prev.stages.requirements,
          status: "not_started",
        },
      },
    }));

    try {
      const specVersion = await researchApi.getTrackRequirements(trackId);

      if (specVersion) {
        // Map backend structure to frontend format
        const objective = specVersion.spec_payload.objective || "";

        // Map SpecCriterion objects to frontend criteria format
        const criteria = (specVersion.criteria || []).map((criterion) => ({
          id: criterion.id, // UUID - matches criteria_results keys
          criterion_id: criterion.criterion_id, // Stable identifier for tracking
          label: criterion.label,
          value: criterion.criterion_payload.description || "",
          weight: criterion.weight as 0 | 1 | 2 | 3 | 4 | 5,
          mode: criterion.criterion_type as "strict" | "fuzzy",
          rubric: criterion.criterion_payload.rubric
            ? (criterion.criterion_payload.rubric as FuzzyRubric)
            : undefined,
        }));

        const requirements = {
          objective,
          criteria,
        };

        setState((prev) => ({
          ...prev,
          requirements,
          stages: {
            ...prev.stages,
            requirements: {
              ...prev.stages.requirements,
              status: objective ? "complete" : "not_started",
            },
          },
        }));
      }

      // Set current track ID so save functionality works
      setCurrentTrackId(trackId);
    } catch (error) {
      console.error("Failed to load requirements:", error);
      // Keep the reset state (empty requirements) on error
    }
  }, []);

  // Save requirements to backend
  const saveRequirements = useCallback(
    async (trackId: string) => {
      if (!trackId) return;

      setRequirementsSaving(true);
      try {
        await researchApi.updateTrackRequirements(trackId, state.requirements);
        // Reload requirements to get the new version
        await loadRequirements(trackId);
        // Refresh activities to show the requirements update
        await activitiesQuery.refetch();
        // Exit edit mode on successful save
        setRequirementsEditMode(false);
      } catch (error) {
        console.error("Failed to save requirements:", error);
        // Keep edit mode open on error so user can retry
      } finally {
        setRequirementsSaving(false);
      }
    },
    [state.requirements, loadRequirements, activitiesQuery],
  );

  // Cancel requirements editing
  const cancelRequirementsEdit = useCallback(
    async (trackId: string) => {
      if (!trackId) return;
      // Reload requirements from backend to discard changes
      await loadRequirements(trackId);
      setRequirementsEditMode(false);
    },
    [loadRequirements],
  );

  useEffect(() => {
    if (entitiesQuery.data) {
      const entities: ResearchEntity[] = entitiesQuery.data;
      setState((prev) => ({
        ...prev,
        entities,
        stages: deriveStages(entities, prev.activeWork, prev.stages),
      }));
    }
  }, [entitiesQuery.data]);

  useEffect(() => {
    if (activitiesQuery.data) {
      setState((prev) => {
        const activeWork = activitiesQuery.data.activeWork ?? prev.activeWork;
        return {
          ...prev,
          agentActivities: activitiesQuery.data.activities,
          agentState: activitiesQuery.data.agentState,
          activeWork,
          // In-flight work drives the Discovery/Evaluation stage status, so a
          // run starting or finishing must re-derive it without waiting for
          // the slower entity poll.
          stages: deriveStages(prev.entities, activeWork, prev.stages),
        };
      });
    }
  }, [activitiesQuery.data]);

  // When activities detect a change (new decision, new entity, run status
  // transition), immediately invalidate the entities query so the user sees
  // fresh data without waiting for the slower entity poll interval.
  useEffect(() => {
    const lastModified = activitiesQuery.data?.lastModified ?? null;
    if (
      lastModified &&
      prevLastModifiedRef.current !== null &&
      lastModified !== prevLastModifiedRef.current &&
      currentTrackId
    ) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.research.trackEntities(currentTrackId),
      });
    }
    prevLastModifiedRef.current = lastModified;
  }, [activitiesQuery.data?.lastModified, currentTrackId, queryClient]);

  // Update entity status
  const updateEntityStatus = useCallback(
    async (
      entityId: string,
      status: WorkflowStatus,
      rejectionReason?: string,
    ) => {
      // Optimistic update
      const entity = state.entities.find((e) => e.id === entityId);
      if (!entity) return;
      setState((prev) => ({
        ...prev,
        entities: prev.entities.map((e) =>
          e.id === entityId
            ? {
                ...e,
                workflowStatus: status,
                rejectionReason,
                lastUpdated: new Date().toISOString(),
              }
            : e,
        ),
      }));

      // Update backend
      try {
        await researchApi.bulkUpdateDecisions({
          entity_ids: [entityId],
          decision_state: status,
          decision_meta: rejectionReason ? { reason: rejectionReason } : {},
        });

        // Recalculate stage status/counts based on updated entities
        setState((prev) => ({
          ...prev,
          stages: deriveStages(prev.entities, prev.activeWork, prev.stages),
        }));
        // Refresh activities to show the decision
        await activitiesQuery.refetch();
      } catch (error) {
        console.error("Failed to update entity status:", error);
        // Revert optimistic update on error
        setState((prev) => ({
          ...prev,
          entities: prev.entities.map((e) => (e.id === entityId ? entity : e)),
        }));
      }
    },
    [state.entities, activitiesQuery],
  );

  // Bulk update entity statuses
  const bulkUpdateEntityStatus = useCallback(
    async (entityIds: string[], status: WorkflowStatus) => {
      // Optimistic update
      setState((prev) => ({
        ...prev,
        entities: prev.entities.map((e) =>
          entityIds.includes(e.id)
            ? {
                ...e,
                workflowStatus: status,
                lastUpdated: new Date().toISOString(),
              }
            : e,
        ),
      }));

      // Update backend
      try {
        await researchApi.bulkUpdateDecisions({
          entity_ids: entityIds,
          decision_state: status,
          decision_meta: {},
        });

        // Recalculate stage status/counts based on updated entities
        setState((prev) => ({
          ...prev,
          stages: deriveStages(prev.entities, prev.activeWork, prev.stages),
        }));
        // Refresh activities to show the decisions
        await activitiesQuery.refetch();
      } catch (error) {
        console.error("Failed to bulk update entity statuses:", error);
        // Reload to revert optimistic update
        await entitiesQuery.refetch();
      }
    },
    [currentTrackId, entitiesQuery, activitiesQuery],
  );

  // Set agent state
  const setAgentState = useCallback((agentState: AgentState) => {
    setState((prev) => ({
      ...prev,
      agentState,
    }));
  }, []);

  // Add agent activity
  const addAgentActivity = useCallback(
    (activity: Omit<AgentActivity, "id" | "timestamp">) => {
      setState((prev) => ({
        ...prev,
        agentActivities: [
          {
            ...activity,
            id: `act-${Date.now()}`,
            timestamp: new Date().toISOString(),
          },
          ...prev.agentActivities,
        ],
      }));
    },
    [],
  );

  // Generate long list - trigger discovery workflow.
  // targetCandidates is the single user-facing knob: how many candidates to aim
  // for this run. The backend derives the scan/query budget from it, so a larger
  // target reaches more candidates without needing to start discovery repeatedly.
  const generateLongList = useCallback(async (targetCandidates?: number) => {
    if (!currentTrackId) {
      console.error("No track ID available to trigger discovery");
      return;
    }

    // Optimistically set agent state to running immediately
    setState((prev) => ({
      ...prev,
      agentState: "running",
    }));

    try {
      await researchApi.triggerDiscovery(
        currentTrackId,
        targetCandidates != null ? { target_candidates: targetCandidates } : undefined,
      );
      // Refetch activities to get the pending run
      await activitiesQuery.refetch();
    } catch (error) {
      console.error("Failed to trigger discovery workflow:", error);
      const axiosErr = error as AxiosError<{ message?: string }>;
      if (axiosErr?.response?.status === 402) {
        showNotification(
          <>
            You don&apos;t have enough credits to run this action. Please
            contact{" "}
            <a href="mailto:support@alpha-affinity.com" style={{ color: "inherit", textDecoration: "underline" }}>
              support@alpha-affinity.com
            </a>{" "}
            to top up your credit balance.
          </>,
          "warning",
        );
      } else {
        showNotification("Failed to start discovery. Please try again.", "error");
      }
      // Revert to idle on error
      setState((prev) => ({
        ...prev,
        agentState: "idle",
      }));
      await activitiesQuery.refetch();
    }
  }, [currentTrackId, activitiesQuery, showNotification]);

  // Trigger evaluation workflow for selected entities
  const triggerEvaluation = useCallback(
    async (trackEntityIds: string[]) => {
      if (!currentTrackId) {
        console.error("No track ID available to trigger evaluation");
        return;
      }

      // Optimistically set agent state to running immediately
      setState((prev) => ({
        ...prev,
        agentState: "running",
      }));

      try {
        const result = await researchApi.triggerEvaluation(currentTrackId, {
          track_entity_ids: trackEntityIds,
        });
        // Surface candidates skipped because they were already in flight.
        const skipped: string[] | undefined = result?.skipped;
        if (skipped && skipped.length > 0) {
          showNotification(
            `${skipped.length} candidate${skipped.length === 1 ? "" : "s"} already being evaluated — skipped.`,
            "warning",
          );
        }
        // Refetch so the new queued candidates + counts show immediately.
        await entitiesQuery.refetch();
        await activitiesQuery.refetch();
      } catch (error) {
        console.error("Failed to trigger evaluation workflow:", error);
        const axiosErr = error as AxiosError<{ message?: string }>;
        if (axiosErr?.response?.status === 402) {
          showNotification(
            <>
              You don&apos;t have enough credits to run this action. Please
              contact{" "}
              <a href="mailto:support@alpha-affinity.com" style={{ color: "inherit", textDecoration: "underline" }}>
                support@alpha-affinity.com
              </a>{" "}
              to top up your credit balance.
            </>,
            "warning",
          );
        } else {
          showNotification(
            "Failed to start evaluation. Please try again.",
            "error",
          );
        }
        // Revert to idle on error
        setState((prev) => ({
          ...prev,
          agentState: "idle",
        }));
        await activitiesQuery.refetch();
      }
    },
    [currentTrackId, entitiesQuery, activitiesQuery, showNotification],
  );

  // Cancel one candidate's in-flight evaluation (siblings keep running).
  const cancelEvaluation = useCallback(
    async (entityId: string) => {
      try {
        await researchApi.cancelEvaluation(entityId);
        await entitiesQuery.refetch();
        await activitiesQuery.refetch();
      } catch (error) {
        console.error("Failed to cancel candidate evaluation:", error);
      }
    },
    [entitiesQuery, activitiesQuery],
  );

  // Cancel every in-flight evaluation for the track ("Stop all").
  const cancelAllEvaluations = useCallback(async () => {
    if (!currentTrackId) return;
    try {
      await researchApi.cancelAllEvaluations(currentTrackId);
      await entitiesQuery.refetch();
      await activitiesQuery.refetch();
    } catch (error) {
      console.error("Failed to cancel evaluations:", error);
    }
  }, [currentTrackId, entitiesQuery, activitiesQuery]);

  // Cancel any in-flight discovery run(s) for the track.
  const cancelDiscovery = useCallback(async () => {
    const runs = state.activeWork.discovery;
    if (runs.length === 0) return;
    try {
      await Promise.all(runs.map((r) => researchApi.cancelRun(r.run_id)));
      await activitiesQuery.refetch();
    } catch (error) {
      console.error("Failed to cancel discovery:", error);
    }
  }, [state.activeWork.discovery, activitiesQuery]);

  // Select entity for detail view
  const selectEntity = useCallback((entityId: string | undefined) => {
    setState((prev) => ({
      ...prev,
      selectedEntityId: entityId,
    }));
  }, []);

  // Set overlay mode
  const setOverlayMode = useCallback(
    (mode: "profile" | "research" | undefined) => {
      setState((prev) => ({
        ...prev,
        overlayMode: mode,
      }));
    },
    [],
  );

  // Reset to initial state
  const resetWorkflow = useCallback(() => {
    const initialState: WorkflowState = {
      projectTitle: "Research Project",
      requirements: {
        objective: "",
        criteria: [],
      },
      entities: [],
      agentState: "idle",
      agentActivities: [],
      activeWork: { evaluation: { queued: 0, running: 0 }, discovery: [] },
      stages: {
        requirements: { stage: "requirements", status: "complete", counts: {} },
        longList: {
          stage: "longList",
          status: "not_started",
          counts: { total: 0, approved: 0, rejected: 0 },
        },
        shortList: {
          stage: "shortList",
          status: "not_started",
          counts: { shortlisted: 0 },
        },
        finalSelection: {
          stage: "finalSelection",
          status: "not_started",
          counts: { final: 0 },
        },
      },
      currentStage: "longList",
    };
    setState(initialState);
  }, []);

  return {
    state,
    loading: entitiesQuery.isLoading,
    activitiesLoading: activitiesQuery.isLoading,
    currentTrackId,
    requirementsEditMode,
    requirementsSaving,
    actions: {
      loadEntities: (trackId: string) => {
        setCurrentTrackId(trackId);
        // Pull immediately — callers use this right after an action that
        // changed the list (add by domain, remove duplicate), and waiting
        // for the 30s idle poll reads as the action having failed.
        return queryClient.invalidateQueries({
          queryKey: queryKeys.research.trackEntities(trackId),
        });
      },
      loadRequirements,
      loadActivities: (trackId: string) => {
        setCurrentTrackId(trackId);
        return Promise.resolve();
      },
      updateRequirements,
      updateObjective,
      addCriterion,
      updateCriterion,
      removeCriterion,
      saveRequirements,
      cancelRequirementsEdit,
      setRequirementsEditMode,
      setCurrentStage,
      updateEntityStatus,
      bulkUpdateEntityStatus,
      setAgentState,
      addAgentActivity,
      generateLongList,
      triggerEvaluation,
      cancelEvaluation,
      cancelAllEvaluations,
      cancelDiscovery,
      selectEntity,
      setOverlayMode,
      resetWorkflow,
    },
  };
};
